(ns frontend.view.entity-merge.associations
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.utils :as fu]
   [frontend.view.entity-merge.components :as components]
   [frontend.view.entity-merge.events :as events]
   [potpuri.core :as potpuri :refer [fn->]]
   [reagent.core :as r]
   [frontend.state.current-session :as session]))

(defmethod components/step-info :associations
  [_signals [_step-kw step-params]]
  (loc/tr :entity-merge-step/associations-info
      [(->> (loc/tr step-params)
            (string/lower-case))]))

(defn association-group
  [entity-type association-type association-elements]
  [:div.review-association-group
   (doall
    (for [[{:keys [db/id] :as _association}
           {other-entity-type :entity/type :as other}]
          association-elements]
      ^{:key (str "association-" id "-" (:db/id other))}
      [:div.associated-entity
       (loc/tr :entity-linked-to
           [(loc/tr entity-type)
            (string/lower-case (loc/tr other-entity-type))])
       [:span.text-bold (fu/resolve-name other)]
       (loc/tr :entity-linked-to-separator)
       (loc/tr :label-for [(loc/tr :type)])
       [:span.text-bold (loc/tr association-type)]]))])

(defn review
  [{:keys [state r-target-id r-source-id r-target r-source entity-type] :as signals}
   associated-entity-type]
  (let [title (loc/tr :entity-type-associations [(loc/tr associated-entity-type)])
        step-kw :associations
        ;;set of explicitly rejected values instead of selected values - reduces risks of losing data
        rejection-path [:rejection step-kw associated-entity-type]
        rejection (get-in @state rejection-path)
        source @r-source
        target @r-target
        target-id @r-target-id
        source-id @r-source-id
        target-associations (->> (fu/related-entity-associations associated-entity-type target-id)
                                 (map (fn [as]
                                        [as (fu/association-target target as)])))
        source-associations (->> (fu/related-entity-associations associated-entity-type source-id)
                                 (map (fn [as]
                                        [as (fu/association-target source as)])))
        associations (->> (concat source-associations target-associations)
                          (remove (partial events/association-between? target-id source-id))
                          (remove (fn [[{:keys [db/id] :as _association} _other-entity]]
                                    (contains? rejection id))))
        grouped-associations (->> associations
                                  (group-by (fn-> first :entity-association/type)))]
    (when (seq associations)
      [components/review-card signals
       {:title          title
        :collapsed-path [:review-associations associated-entity-type]
        :content
        [:div.review-associated-entities
         (doall
          (for [[association-type association-elements] grouped-associations]
            ^{:key (str "associations-group-" association-type)}
            [association-group entity-type association-type association-elements]))]}])))

(defn person-info
  [person]
  (let [email (->> [:person/universal-email-address :person/other-email-addresses]
                   (keep (fn [email-field]
                           (get person email-field)))
                   first)
        phone-number (->> [:person/universal-phone-number :person/mobile-phone-number
                           :person/work-phone-number]
                          (keep (fn [phone-field]
                                  (get person phone-field)))
                          first)]
    [:<>
     (when-not (cu/blank-value? email)
       [:div email])
     (when-not (cu/blank-value? phone-number)
       [:div phone-number])]))

(defn deal-info
  [deal]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        status-fs (registry/get-field-schema tenant-schema :oppo/status)
        status (fu/display-data tenant-schema tenant :oppo/status status-fs deal)
        stage-fs (registry/get-field-schema tenant-schema :oppo/stage)
        stage (fu/display-data tenant-schema tenant :oppo/stage stage-fs deal)]
    [:<>
     [:div
      [:strong (loc/tr :label-for [(loc/tr :oppo/status)])]
      status]
     [:div
      [:strong (loc/tr :label-for [(loc/tr :oppo/stage)])]
      stage]]))

(defn has-deal-info?
  [deal]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        status-fs (registry/get-field-schema tenant-schema :oppo/status)
        status (fu/display-data tenant-schema tenant :oppo/status status-fs deal)
        stage-fs (registry/get-field-schema tenant-schema :oppo/stage)
        stage (fu/display-data tenant-schema tenant :oppo/stage stage-fs deal)]
    (->> [status stage]
         (not-every? cu/blank-value?))))

(defn has-person-info?
  [person]
  (let [email (->> [:person/universal-email-address :person/other-email-addresses]
                   (keep (fn [email-field]
                           (get person email-field)))
                   first)
        phone-number (->> [:person/universal-phone-number :person/mobile-phone-number
                           :person/work-phone-number]
                          (keep (fn [phone-field]
                                  (get person phone-field)))
                          first)]
    (->> [email phone-number]
         (not-every? cu/blank-value?))))

(defn has-entity-info?
  [entity]
  (let [entity-type (:entity/type entity)]
    (case entity-type
      :entity.type/person (has-person-info? entity)
      :entity.type/oppo (has-deal-info? entity)
      nil)))

(defn entity-info
  [entity]
  (when (has-entity-info? entity)
    (let [entity-type (:entity/type entity)]
      (case entity-type
        :entity.type/person [person-info entity]
        :entity.type/oppo [deal-info entity]
        nil))))

(defn has-comments?
  [{:entity-association/keys [source-comment target-comment] :as _association}]
  (->> [source-comment target-comment]
       (not-every? cu/blank-value?)))

(defn has-details?
  [association entity]
  (let [other (fu/association-target entity association)]
    (or (has-comments? association)
        (has-entity-info? other))))

(defn comment-details
  [{:entity-association/keys [source-comment target-comment] :as _association}]
  [:<>
   (when-not (cu/blank-value? source-comment)
     [:div.mt-2.title-h5.text-color--light-grey
      [:span.fas.fa-info-circle.mr-2]
      source-comment])
   (when-not (cu/blank-value? target-comment)
     [:div.mt-2.title-h5.text-color--light-grey
      [:span.fas.fa-info-circle.mr-2]
      target-comment])])

(defn details
  [association entity]
  (let [other (fu/association-target entity association)]
    [:<>
     (when (has-entity-info? other)
       [entity-info other])
     (when (has-comments? association)
       [comment-details association])]))

(defn associations-list
  [_]
  (let [local-state (r/atom {})
        toggle-show-details!
        (fn [k]
          (swap! local-state update-in [k :show-details?] not))]
    (fn [{:keys [entity associations rejection toggle!]}]
      [:<>
       (doall
        (for [{association-id :db/id :as association} associations
              :let [selected? (not (contains? rejection association-id))
                    other (fu/association-target entity association)
                    show-details? (get-in @local-state [association-id :show-details?])
                    display-value (fu/resolve-name other)]]
          ^{:key (str "source-association-" association-id)}
          [:div
           [:div
            {:class-name (fu/cs "asset-updater-table__current-value mb-2"
                                (when selected?
                                  "selected"))
             :on-click   (fn [_]
                           (toggle! association-id))}
            (if selected?
              [:i.fas.fa-check-circle.mr-2]
              [:i.fal.fa-circle.mr-2])
            [:div
             [:div display-value]

             (when show-details?
               [details association entity])]]
           (when (has-details? association entity)
             [:div.text-color--info.title-h5.pointer
              {:on-click (partial toggle-show-details! association-id)}
              (if show-details?
                (loc/tr :show-less)
                (loc/tr :show-details))])]))])))

(defn both?
  [e1-id e2-id association]
  (let [{:entity-association/keys [target-entity source-entity]} association]
    (= #{e1-id e2-id} #{target-entity source-entity})))

(defn preselect!
  [{:keys [state r-target-id r-source-id] :as _signals}]
  (let [[step-kw associated-entity-type] (:current-step @state)
        ;;set of explicitly rejected values instead of selected values - reduces risks of losing data
        rejection-path [:rejection step-kw associated-entity-type]
        rejection (get-in @state rejection-path)
        no-selection? (nil? rejection)
        target-id @r-target-id
        source-id @r-source-id
        source-associations (fu/related-entity-associations associated-entity-type source-id)
        target-associations (fu/related-entity-associations associated-entity-type target-id)
        to-reject (->> source-associations
                       (map :db/id)
                       set)
        ;;skip associations between merge target and source:
        both-associations (->> (concat source-associations target-associations)
                               (filter (partial both? target-id source-id))
                               (map :db/id)
                               set)
        to-reject (set/union to-reject both-associations)]
    (when no-selection?
      (swap! state assoc-in rejection-path to-reject))))

(defn view
  [signals]
  (preselect! signals)
  (fn [{:keys [state entity-type r-target r-source r-target-id r-source-id] :as _signals}]
    (let [[step-kw step-args] (:current-step @state)
          associated-entity-type step-args
          target-id @r-target-id
          source-id @r-source-id
          source @r-source
          target @r-target
          source-associations
          (->> @(r/track fu/related-entity-associations associated-entity-type source-id)
               (remove (partial events/association-between? target-id source-id))
               (group-by :entity-association/type))
          target-associations
          (->> @(r/track fu/related-entity-associations associated-entity-type target-id)
               (remove (partial events/association-between? target-id source-id))
               (group-by :entity-association/type))
          association-types (->> [source-associations target-associations]
                                 (mapcat keys)
                                 distinct)
          rejection-path [:rejection step-kw associated-entity-type]
          rejection (get-in @state rejection-path)]
      [:div.asset-updater-table__fields.py-3
       (doall
        (for [association-type association-types
              :let [association-name (loc/tr association-type)
                    source-associations (get source-associations association-type)
                    target-associations (get target-associations association-type)
                    association-label (cond (and (= entity-type :entity.type/person)
                                                 (= associated-entity-type :entity.type/person))
                                            (loc/tr :person-person-association [association-name])

                                            (= entity-type :entity.type/person)
                                            (loc/tr :person-association [association-name])

                                            :else
                                            association-name)]]
          ^{:key (str "merge-field-" association-type)}
          [:div.asset-updater-table__field
           [:div.text-center.text-bold.py-3
            {:title association-label}
            association-label]
           [:div.asset-updater-table__compare
            [:div
             {:class-name "mt-4 grid grid-cols-2 items-center gap-4"}
             [:div
              [associations-list
               {:state        state
                :entity       source
                :associations source-associations
                :rejection    rejection
                :toggle!      (r/partial events/toggle! state rejection-path)}]]
             [:div
              [associations-list
               {:state        state
                :entity       target
                :associations target-associations
                :rejection    rejection
                :toggle!      (r/partial events/toggle! state rejection-path)}]]]]]))])))
